import Nav from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../lib/Seo";
import RevealText from "../lib/RevealText";
import Reveal from "../lib/Reveal";
import { Link } from "react-router-dom";
import { ArrowUpRight, Camera } from "lucide-react";

const values = [
  {
    num: "01",
    title: "Composed, not sold",
    body: "We do not publish prices. We do not resell packages. Every journey is composed privately, once, for the guest whose dates and dreams begin it.",
  },
  {
    num: "02",
    title: "One planner, one guide",
    body: "You will speak to the same person from first message to homecoming. On the ground, one senior guide accompanies you throughout the journey.",
  },
  {
    num: "03",
    title: "Small camps, deep trust",
    body: "Every lodge we work with, we have visited. Some we have known for a decade. We turn down more than we accept.",
  },
  {
    num: "04",
    title: "Regenerative by default",
    body: "Not marketing. Method. Every journey supports the conservancies and communities on whose land we travel — measurably, monitored, audited.",
  },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Njia Safaris — The Path, The Philosophy"
        description="Njia Safaris designs ultra-luxury private safaris across East Africa. A small team of planners composing journeys privately, once, for guests who arrive already knowing what they want."
        path="/about"
      />
      <Nav />

      {/* Hero */}
      <section className="pt-40 pb-16 md:pt-56 md:pb-24 container-x" data-testid="about-hero">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-8">
            <p className="eyebrow mb-6">About Njia · The path</p>
            <RevealText
              as="h1"
              className="h-display text-[12vw] md:text-[8vw] leading-none"
              lines={["A quieter", <em key="e" className="italic text-[color:var(--forest)]">safari house.</em>]}
            />
          </div>
        </div>
      </section>

      {/* Manifesto image + text */}
      <section className="container-x pb-24" data-testid="about-manifesto">
        <div className="grid md:grid-cols-12 gap-10">
          <Reveal className="md:col-span-5">
            <div className="img-frame aspect-[3/4]">
              <img
                src="https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=1800&q=85"
                alt="Sunset across the East African plains"
              />
            </div>
          </Reveal>
          <div className="md:col-span-6 md:col-start-7">
            <p className="font-display text-3xl md:text-4xl leading-tight mb-8">
              Njia is a small safari house — a handful of planners in Noida, Nairobi and Arusha,
              composing journeys for guests who already know East Africa is what they want.
            </p>
            <p className="text-lg leading-relaxed text-[color:var(--ink)]/85 mb-6 max-w-xl">
              We do not do groups. We do not do gift vouchers. We do not do multi-continent whistle-stops.
              We do East Africa, at close range, at the highest possible standard, one guest at a time.
            </p>
            <p className="text-lg leading-relaxed text-[color:var(--ink)]/85 max-w-xl">
              The word <em className="italic font-italic-display text-[color:var(--forest)]">njia</em> means
              path, or way, in Swahili. It is what we do — compose a path — through people, land, and hours
              that could only ever be yours.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-forest py-24 md:py-32" data-testid="about-values">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-4">
              <p className="eyebrow mb-4">Our method</p>
              <RevealText
                as="h2"
                className="h-display text-4xl md:text-5xl text-[color:var(--ivory)]"
                lines={["How we", <em key="e" className="italic text-[color:var(--gold-3)]">work.</em>]}
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-10 md:gap-16">
            {values.map((v, i) => (
              <Reveal key={v.num} delay={i * 0.06}>
                <div className="border-t border-[color:var(--line-invert)] pt-6">
                  <span className="chapter-num">{v.num}</span>
                  <h3 className="font-display text-3xl md:text-4xl text-[color:var(--ivory)] mt-4">
                    {v.title}
                  </h3>
                  <p className="mt-4 text-[color:var(--sand)] max-w-md leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Team blurb */}
      <section className="section-y container-x" data-testid="about-team">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-4">
            <p className="eyebrow mb-4">The people</p>
            <RevealText
              as="h2"
              className="h-display text-4xl md:text-5xl"
              lines={["Small,", <em key="e" className="italic text-[color:var(--forest)]">on purpose.</em>]}
            />
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="text-lg leading-relaxed max-w-xl mb-6">
              Njia is planned from Noida, India — our head office — and delivered on the ground by senior
              guides in Nairobi and Arusha, most of whom we have worked with for a decade or more.
            </p>
            <p className="text-lg leading-relaxed max-w-xl">
              We could scale. We choose not to. The number of journeys we compose each year is deliberately
              limited to protect the standard we hold ourselves to.
            </p>
          </div>
        </div>
      </section>

      {/* THE INSPIRATION BEHIND NJIA — Jeet Aman */}
      <section className="section-dark section-y" data-testid="about-inspiration">
        <div className="container-x">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <p className="eyebrow eyebrow-invert mb-6">The inspiration behind Njia</p>
              <RevealText
                as="h2"
                className="h-display text-5xl md:text-6xl text-[color:var(--ivory)]"
                lines={["A mentor,", <em key="e" className="italic text-[color:var(--gold-3)]">a lens.</em>]}
              />
            </div>
          </div>

          <div className="grid md:grid-cols-12 gap-10 md:gap-16 items-start">
            {/* Portrait card */}
            <Reveal className="md:col-span-5">
              <div className="relative">
                <div className="img-frame aspect-[4/5] shadow-editorial">
                  <img
                    src="https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=1400&q=80"
                    alt="Portrait of Jeet Aman"
                    className="grayscale contrast-[1.05]"
                  />
                </div>
                <div
                  className="absolute -bottom-4 -right-4 md:-bottom-6 md:-right-6 border px-5 py-3 backdrop-blur-sm bg-[color:var(--ink)]/70"
                  style={{ borderColor: "var(--gold)" }}
                >
                  <p className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-3)]">Mentor · Creative Visionary</p>
                </div>
              </div>
            </Reveal>

            {/* Bio */}
            <div className="md:col-span-6 md:col-start-7">
              <div className="flex items-center gap-3 mb-6">
                <Camera size={18} strokeWidth={1.4} className="text-[color:var(--gold-3)]" />
                <span className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-3)]">
                  Jeet Aman · Wildlife Photographer
                </span>
              </div>
              <p className="font-display text-3xl md:text-4xl leading-tight text-[color:var(--ivory)] mb-8">
                Jeet Aman is an acclaimed wildlife photographer whose ground-level, cinematic approach to
                the wild inspired the ethos of Njia Safaris.
              </p>
              <p className="text-lg leading-relaxed text-[color:var(--sand)] max-w-xl mb-6">
                From the dust of Amboseli to the ice of the Arctic, his frames are composed with the same
                discipline — patience, respect, and a refusal to intrude on the moment before it is given.
                Big cats. Elephants at eye-level. Polar wildlife on the white north.
              </p>
              <p className="text-lg leading-relaxed text-[color:var(--sand)] max-w-xl mb-10">
                It is his method — arrive quiet, wait long, take only what is offered — that we, in turn,
                composed into a way of travelling. Njia was built around it.
              </p>

              <div className="hairline hairline-invert mb-8" />

              <blockquote className="font-italic-display text-xl md:text-2xl text-[color:var(--ivory)] leading-snug mb-6 max-w-xl">
                &ldquo;Every frame is a story of patience, respect, and the raw beauty of the wild.&rdquo;
              </blockquote>
              <p className="mono text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold-3)] mb-10">
                — Jeet Aman
              </p>

              <Link to="/lens-of-jeet-aman" className="btn-gold" data-testid="about-inspiration-cta">
                Through the lens of Jeet Aman
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-dark py-24 md:py-32" data-testid="about-cta">
        <div className="container-x grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <RevealText
              as="h2"
              className="h-display text-5xl md:text-7xl text-[color:var(--ivory)]"
              lines={["Speak with", <em key="e" className="italic text-[color:var(--gold-3)]">a planner.</em>]}
            />
          </div>
          <div className="md:col-span-5">
            <p className="text-lg leading-relaxed text-[color:var(--sand)] mb-8 max-w-md">
              Every conversation begins in writing, and continues from there. We reply within one working day.
            </p>
            <Link to="/contact" className="btn-gold" data-testid="about-cta-contact">
              Begin the conversation
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
